import { useEffect, useState } from "react";
import { HauffeLoader } from "../components/BrandLogo";
import { Layout } from "../components/Layout";
import { Panel, Stat, StatusBadge } from "../components/Panel";
import { DealWinPlanCard, GuidedPlanCard } from "../components/PricingPlanCards";
import { getWorkspaceAccessMessage, workspaceStatusLabel } from "../lib/adminActions";
import {
  configureRuntimeOwnerPerspective,
  fetchRuntimeBillingEntitlement,
  fetchRuntimeBillingPlans,
  fetchRuntimeWorkspace,
  openRuntimeBillingPortal,
  requestRuntimeWorkspaceContentDeletion,
  startRuntimeBillingCheckout,
  type RuntimeBillingPlan,
  type RuntimeBillingStatus,
  type RuntimeWorkspaceOverview
} from "../lib/api/runtimeWorkspaceClient";
import { retentionNotice } from "../lib/contentLibrary";
import { calculateCheckoutAmount, getBillingPlanAction, getDealWinEnginePlans, isBillingPlanId, type CheckoutPurpose } from "../lib/billing/plans";
import { useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { readBilingualText } from "../lib/i18n/bilingualContent";
import { buildInviteLink } from "../lib/invite";
import { getPackageLabel, getRemainingDays } from "../lib/packageLimits";
import { getExecutivePerspective, isBillablePerspective, valueRoundCanAcceptInput } from "../lib/valueWorkflow";
import { deriveWorkspaceModuleAccess } from "../lib/workspaceAccess";
import type { PerspectiveType } from "../types";

export function Workspace({ section = "overview" }: { section?: "overview" | "plans" } = {}) {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "Company Owner",
    title: "Customer Value Workspace",
    description: "Collect department perspectives, request the HAUFFE OS Evaluation and prepare a usable Customer Value Calculation from it.",
    loadingTitle: "Workspace is loading",
    loadingLabel: "Customer Value Workspace is loading",
    loadError: "Workspace data could not be loaded.",
    loadErrorTitle: "Workspace could not be loaded",
    noWorkspaceTitle: "No workspace available yet.",
    noWorkspaceBody: "There is currently no workspace stored for your access.",
    setup: "Set up workspace",
    workspaceOverview: "Workspace overview",
    manageDepartments: "Manage departments",
    evaluation: "HAUFFE OS Evaluation",
    calculation: "Customer Value Calculation",
    package: "Package",
    status: "Workspace status",
    remainingDays: "Remaining days",
    invitedDepartments: "Departments invited",
    submitted: "Submitted",
    created: "Created",
    open: "Open",
    prepared: "Prepared",
    complete: "Complete",
    incomplete: "Incomplete",
    currentGoal: "Current goal",
    noGoal: "No goal has been stored yet.",
    workspaceNotice: "Workspace notice",
    openTasks: "Open Tasks",
    nextStep: "Next step",
    resultAccess: "Customer Value Calculation",
    viewCalculation: "View Customer Value Calculation",
    createCalculation: "Create Customer Value Calculation",
    noDepartments: "No departments created yet.",
    inviteFirst: "Invite the first departments and collect department perspectives.",
    requestEvaluation: "Request the HAUFFE OS Evaluation from the submitted department perspectives.",
    prepareCalculation: "Prepare the Customer Value Calculation from the HAUFFE OS Evaluation.",
    useCalculation: "Use the Customer Value Calculation for further evaluation.",
    stepSetup: "Set up workspace",
    stepDepartments: "Collect department perspectives",
    stepEvaluation: "Request HAUFFE OS Evaluation",
    stepCalculation: "Prepare Customer Value Calculation",
    perspectivesSubmitted: (submitted: number, total: number) => `${submitted} of ${total} department perspectives submitted.`,
    evaluationAvailable: "Available",
    noEvaluation: "No HAUFFE OS Evaluation available yet.",
    calculationPrepared: "Prepared",
    noCalculation: "No Customer Value Calculation prepared yet.",
    notStored: "Not stored",
    retentionTitle: "Content Library & retention",
    deletionRequest: "Request deletion",
    deletionRequested: "Deletion request was recorded.",
    deletionError: "Deletion request could not be recorded.",
    retentionExpired: "Retention expired",
    repairNeeded: "Repair needed",
    active: "Active",
    deletionRequestedStatus: "Deletion requested",
    deleted: "Deleted",
    billingTitle: "Access & billing",
    billingBody: "Productive workspace access is managed through Stripe payments or audited HAUFFE Admin grants.",
    myPlans: "My Plans",
    myPlansBody: "Manage your current access, available modules, extensions and checkout options in one dedicated area.",
    openMyPlans: "Open My Plans",
    billingOverviewTitle: "Current access",
    billingOverviewBody: "Your active plan and billing actions are managed under My Plans so the workspace overview stays focused on the operating workflow.",
    moduleOverviewTitle: "Modules in this workspace",
    moduleOverviewBody: "Booked modules show their operating workflow. Modules that are not booked stay compact and can be added under My Plans.",
    moduleActive: "Booked",
    moduleNotBooked: "Not booked",
    moduleContinue: "Continue",
    bookNow: "Book now",
    guidedModuleActiveText: "Structure customer value, departments, criteria and evaluations for better customer decisions.",
    guidedModuleUpgradeText: "Structure customer value, departments, criteria and evaluations for better customer decisions.",
    dealWinModuleActiveText: "Assess concrete opportunities, detect closing risks and prioritize the next sales levers.",
    dealWinModuleUpgradeText: "Assess concrete opportunities, detect closing risks and prioritize the next sales levers.",
    openDealWinEngine: "Open Deal Win Engine",
    billingAccess: "Productive access",
    billingPlan: "Plan",
    billingSource: "Access source",
    billingPeriod: "Current period",
    billingDepartments: "Department capacity",
    billingStatus: "Billing status",
    accessAllowed: "Active",
    accessBlocked: "Not active",
    openInvoices: "Invoices",
    changePaymentMethod: "Payment method",
    manageSubscription: "Manage subscription",
    cancelSubscription: "Cancel subscription",
    billingPortalError: "Billing portal could not be opened.",
    billingNotAvailable: "Billing information is not available for this workspace yet.",
    billingPlansTitle: "Access plans",
    guidedSectionTitle: "Guided Digital Access for Customer Value",
    dealWinSectionTitle: "Deal Win Engine",
    executiveIncludedShort: "Executive Management always included – does not count as a department.",
    includedUsers: "included users",
    extraUsersAvailable: "Additional users can be added flexibly",
    dealWinSingleBadge: "Focused start",
    dealWinBundleBadge: "Team advantage",
    dealWinSelect: "Choose this package",
    dealWinCheckoutPrepared: "After purchase, the Account Owner manages access and assigns licenses internally.",
    bundleDiscountHint: "Combine Team 3, 5, or 10 with Guided Digital Access and secure a 10% Guided advantage.",
    bundleDiscountActive: "10% bundle advantage on Guided Digital Access is planned.",
    billingCheckoutNote: "Checkout starts a secure Stripe payment. Productive access is activated only after the verified payment confirmation has reached HAUFFE OS.",
    billingPortalUnavailable: "Stripe self-service is available only after a Stripe customer has been created for this workspace. Manual, migrated or support access remains active without Stripe portal access.",
    billingSubscriptionUnavailable: "Subscription actions require an active Stripe subscription.",
    recommended: "Recommended",
    bestValue: "Enterprise",
    netPrice: "net",
    netPerMonth: "net / month",
    includedDepartments: "included departments",
    extraDepartmentPrice: "per additional department",
    extraDepartments: "Additional departments",
    extraDepartmentsProduct: "Additional departments (Guided Digital Access)",
    extraDepartmentsHelp: "Expand the number of available department perspectives for this workspace.",
    extraDepartmentsLabel: "Choose additional departments",
    extraDepartmentOnce: "net per additional department",
    extraDepartmentMonthly: "net monthly per additional department",
    buyAccess: "Select plan",
    renewAccess: "Renew with 15% discount",
    renewAccessSelect: "Select renewal with 15% discount",
    renewAccessCheckout: "Continue to Stripe – renewal with 15% discount",
    upgradeAccess: "Upgrade with 15% discount",
    upgradeAccessSelect: "Select upgrade with 15% discount",
    upgradeAccessCheckout: "Continue to Stripe – 15% upgrade discount",
    reactivateAccess: "Reactivate access",
    currentPlan: "Current plan",
    downgradeUnavailable: "Downgrade after period end",
    monthlyUnavailable: "Bookable 14 days before period end",
    contactSupport: "Contact HAUFFE",
    addDepartments: "Add departments",
    billingSelectionTitle: "Your selection",
    billingAction: "Action",
    newPlan: "New plan",
    basePrice: "Base price",
    addonPrice: "Additional departments",
    discount15: "Discount 15%",
    totalPrice: "Total price",
    vatNote: "plus applicable VAT",
    stripeActivationNote: "Changes become active only after verified Stripe payment confirmation.",
    bookSelection: "Update selection",
    checkoutPrepared: "Checkout is being prepared",
    dealWinSummaryPrepared: "After purchase, the Account Owner manages access and assigns licenses internally.",
    bundleCheckoutPrepared: "10% bundle advantage on Guided Digital Access is planned. Bundle checkout is being prepared.",
    guidedOnlyCheckoutNote: "Currently, only Guided Digital Access is booked through this checkout. Deal Win Engine checkout starts separately.",
    separateCheckoutNote: "Guided Digital Access and Deal Win Engine are currently booked through separate checkouts.",
    dealWinBuyNow: "Buy Deal Win Engine",
    extraDealWinUsers: "Additional Deal Win users",
    extraDealWinUsersProduct: "Additional users (Deal Win Engine)",
    extraDealWinUsersHelp: "Add additional users to the selected Deal Win Engine team package.",
    totalDealWinUsers: "Total Deal Win users",
    dealWinTotalPrice: "Deal Win Engine total",
    checkoutStarting: "Starting checkout...",
    checkoutError: "Checkout could not be started.",
    executiveValueOpenTitle: "Executive contribution to the Value Round is open",
    executiveValueOpenBody: "The executive perspective has been submitted. The executive contribution to the Value Round is still missing before criteria can be completed.",
    answerExecutiveValueRound: "Answer Value Round as Executive",
    openExecutiveValueRound: "Open Executive Value Round",
    valueRoundContribution: "Value Round Contribution",
    valueRoundContributionSubmitted: "Value Round contribution submitted",
    viewEditExecutivePerspective: "View / edit Executive Perspective",
    viewEditValueRoundContribution: "View / edit Value Round Contribution",
    executivePerspectiveOpenTitle: "Executive Perspective is still open",
    executivePerspectiveOpenBody: "The executive perspective exists, but the initial perspective has not been submitted yet.",
    answerExecutivePerspective: "Answer Executive Perspective",
    ownerRoleTitle: "Clarify your own role",
    ownerRoleBody: "The Workspace Owner is organizationally responsible, but not automatically the executive perspective. Choose whether you also contribute a functional perspective yourself.",
    ownerExecutive: "Select Executive Management",
    ownerExecutiveDescription: "I contribute the Executive Management perspective.",
    ownerFunctional: "Select voting functional perspective",
    ownerFunctionalDescription: "I am not Executive Management, but contribute a relevant functional perspective.",
    ownerOrganizer: "Only organize workspace",
    ownerOrganizerDescription: "I manage the workspace organizationally and do not contribute a functional perspective.",
    selectedChoice: "Selected",
    ownerPerspectiveName: "Your perspective / role",
    ownerPerspectiveType: "Perspective type",
    saveOwnerRole: "Save owner role",
    ownerRoleSaved: "Owner role was saved.",
    ownerRoleError: "Owner role could not be saved.",
    answerOwnPerspective: "Answer my perspective",
    ownerCurrentRole: "Current owner perspective",
    organizationalOnly: "Organizational responsibility only",
    votingPerspective: "Voting perspective",
    executiveIncluded: "Executive Management is included and does not count against the department limit.",
    organizerOnlyNote: "You manage this workspace organizationally and do not provide a functional perspective.",
    perspectiveStatus: "Perspective status",
    draft: "Draft",
    submittedStatus: "Submitted",
    perspectiveLabels: {
      department: "Department",
      executive: "Executive Management",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Additional Perspective"
    } satisfies Record<PerspectiveType, string>
  } : {
    eyebrow: "Company Owner",
    title: "Customer Value Workspace",
    description: "Sammeln Sie Abteilungsperspektiven, rufen Sie die HAUFFE OS Auswertung ab und bereiten Sie daraus eine nutzbare Customer Value Calculation vor.",
    loadingTitle: "Workspace wird geladen",
    loadingLabel: "Customer Value Workspace wird geladen",
    loadError: "Workspace-Daten konnten nicht geladen werden.",
    loadErrorTitle: "Workspace konnte nicht geladen werden",
    noWorkspaceTitle: "Noch kein Workspace vorhanden.",
    noWorkspaceBody: "Für Ihren Zugang ist aktuell noch kein Workspace in D1 hinterlegt.",
    setup: "Workspace einrichten",
    workspaceOverview: "Workspace Übersicht",
    manageDepartments: "Abteilungen verwalten",
    evaluation: "HAUFFE OS Auswertung",
    calculation: "Customer Value Calculation",
    package: "Paket",
    status: "Workspace-Status",
    remainingDays: "Verbleibende Tage",
    invitedDepartments: "Abteilungen eingeladen",
    submitted: "Eingereicht",
    created: "erstellt",
    open: "offen",
    prepared: "vorbereitet",
    complete: "Vollständig",
    incomplete: "Offen",
    currentGoal: "Aktuelle Zielsetzung",
    noGoal: "Noch keine Zielsetzung hinterlegt.",
    workspaceNotice: "Workspace-Hinweis",
    openTasks: "Offene Aufgaben",
    nextStep: "Nächster Schritt",
    resultAccess: "Customer Value Calculation",
    viewCalculation: "Customer Value Calculation ansehen",
    createCalculation: "Customer Value Calculation erstellen",
    noDepartments: "Noch keine Abteilungen angelegt.",
    inviteFirst: "Laden Sie die ersten Abteilungen ein und sammeln Sie Abteilungsperspektiven.",
    requestEvaluation: "Rufen Sie aus den eingereichten Abteilungsperspektiven die HAUFFE OS Auswertung ab.",
    prepareCalculation: "Bereiten Sie aus der HAUFFE OS Auswertung die Customer Value Calculation vor.",
    useCalculation: "Nutzen Sie die Customer Value Calculation für die weitere Bewertung.",
    stepSetup: "Workspace einrichten",
    stepDepartments: "Abteilungsperspektiven sammeln",
    stepEvaluation: "HAUFFE OS Auswertung abrufen",
    stepCalculation: "Customer Value Calculation vorbereiten",
    perspectivesSubmitted: (submitted: number, total: number) => `${submitted} von ${total} Abteilungsperspektiven eingereicht.`,
    evaluationAvailable: "Verfügbar",
    noEvaluation: "Noch keine HAUFFE OS Auswertung vorhanden.",
    calculationPrepared: "Vorbereitet",
    noCalculation: "Noch keine Customer Value Calculation vorbereitet.",
    notStored: "Nicht hinterlegt",
    retentionTitle: "Content Library & Aufbewahrung",
    deletionRequest: "Löschung anfragen",
    deletionRequested: "Löschanfrage wurde erfasst.",
    deletionError: "Löschanfrage konnte nicht erfasst werden.",
    retentionExpired: "Aufbewahrung abgelaufen",
    repairNeeded: "Reparatur erforderlich",
    active: "Aktiv",
    deletionRequestedStatus: "Löschung angefragt",
    deleted: "Gelöscht",
    billingTitle: "Zugang & Abrechnung",
    billingBody: "Produktiver Workspace-Zugang wird über bestätigte Stripe-Zahlungen oder auditierte HAUFFE-Admin-Freigaben gesteuert.",
    myPlans: "Meine Pläne",
    myPlansBody: "Verwalten Sie aktuellen Zugang, verfügbare Module, Erweiterungen und Checkout-Optionen in einem eigenen Bereich.",
    openMyPlans: "Meine Pläne öffnen",
    billingOverviewTitle: "Aktueller Zugang",
    billingOverviewBody: "Aktiver Plan und Abrechnungsaktionen liegen unter Meine Pläne, damit die Workspace-Übersicht operativ fokussiert bleibt.",
    moduleOverviewTitle: "Module in diesem Workspace",
    moduleOverviewBody: "Gebuchte Module zeigen ihre echte Arbeitslogik. Nicht gebuchte Module bleiben kompakt und können unter Meine Pläne ergänzt werden.",
    moduleActive: "Gebucht",
    moduleNotBooked: "Nicht gebucht",
    moduleContinue: "Weiterarbeiten",
    bookNow: "Jetzt buchen",
    guidedModuleActiveText: "Strukturiere Customer Value, Abteilungen, Kriterien und Auswertungen für bessere Kundenentscheidungen.",
    guidedModuleUpgradeText: "Strukturiere Customer Value, Abteilungen, Kriterien und Auswertungen für bessere Kundenentscheidungen.",
    dealWinModuleActiveText: "Bewerte konkrete Opportunities, erkenne Abschlussrisiken und priorisiere die nächsten Hebel im Sales-Prozess.",
    dealWinModuleUpgradeText: "Bewerte konkrete Opportunities, erkenne Abschlussrisiken und priorisiere die nächsten Hebel im Sales-Prozess.",
    openDealWinEngine: "Deal Win Engine öffnen",
    billingAccess: "Produktiver Zugang",
    billingPlan: "Plan",
    billingSource: "Zugangsquelle",
    billingPeriod: "Aktuelle Laufzeit",
    billingDepartments: "Abteilungskapazität",
    billingStatus: "Abrechnungsstatus",
    accessAllowed: "Aktiv",
    accessBlocked: "Nicht aktiv",
    openInvoices: "Rechnungen",
    changePaymentMethod: "Zahlungsmethode",
    manageSubscription: "Abo verwalten",
    cancelSubscription: "Abo kündigen",
    billingPortalError: "Das Abrechnungsportal konnte nicht geöffnet werden.",
    billingNotAvailable: "Für diesen Workspace sind noch keine Abrechnungsinformationen verfügbar.",
    billingPlansTitle: "Zugangspläne",
    guidedSectionTitle: "Guided Digital Access für Customer Value",
    dealWinSectionTitle: "Deal Win Engine",
    executiveIncludedShort: "Geschäftsleitung immer inklusive – zählt nicht als Abteilung.",
    includedUsers: "inklusive Zugänge",
    extraUsersAvailable: "Weitere Zugänge flexibel ergänzbar",
    dealWinSingleBadge: "Fokussierter Einstieg",
    dealWinBundleBadge: "Teamvorteil",
    dealWinSelect: "Dieses Paket wählen",
    dealWinCheckoutPrepared: "Nach dem Kauf verwaltet der Account Owner die Zugänge und weist Lizenzen intern zu.",
    bundleDiscountHint: "Team 3, 5 oder 10 mit Guided Digital Access kombinieren und 10 % Vorteil auf Guided sichern.",
    bundleDiscountActive: "10 % Bundle-Vorteil auf Guided Digital Access vorgesehen.",
    billingCheckoutNote: "Der Checkout startet eine sichere Stripe-Zahlung. Produktiver Zugriff wird erst aktiviert, wenn die verifizierte Zahlungsbestätigung HAUFFE OS erreicht hat.",
    billingPortalUnavailable: "Stripe-Self-Service ist erst verfügbar, wenn für diesen Workspace ein Stripe-Kunde existiert. Manuelle, migrierte oder Support-Zugänge bleiben auch ohne Stripe-Portal aktiv.",
    billingSubscriptionUnavailable: "Abo-Aktionen benötigen eine aktive Stripe-Subscription.",
    recommended: "Empfohlen",
    bestValue: "Enterprise",
    netPrice: "netto",
    netPerMonth: "netto / Monat",
    includedDepartments: "inklusive Abteilungen",
    extraDepartmentPrice: "je zusätzliche Abteilung",
    extraDepartments: "Zusätzliche Abteilungen",
    extraDepartmentsProduct: "Zusätzliche Abteilungen (Guided Digital Access)",
    extraDepartmentsHelp: "Erweitern Sie die Anzahl der verfügbaren Abteilungsperspektiven für diesen Workspace.",
    extraDepartmentsLabel: "Anzahl zusätzlicher Abteilungen wählen",
    extraDepartmentOnce: "netto je zusätzliche Abteilung",
    extraDepartmentMonthly: "netto monatlich je zusätzliche Abteilung",
    buyAccess: "Plan auswählen",
    renewAccess: "Mit 15% Rabatt verlängern",
    renewAccessSelect: "Verlängerung mit 15% Rabatt auswählen",
    renewAccessCheckout: "Weiter zu Stripe – Verlängerung mit 15% Rabatt",
    upgradeAccess: "Upgrade mit 15% Rabatt",
    upgradeAccessSelect: "Upgrade mit 15% Rabatt auswählen",
    upgradeAccessCheckout: "Weiter zu Stripe – Upgrade mit 15% Rabatt",
    reactivateAccess: "Zugang reaktivieren",
    currentPlan: "Aktueller Plan",
    downgradeUnavailable: "Downgrade nach Laufzeitende",
    monthlyUnavailable: "Ab 14 Tage vor Laufzeitende buchbar",
    contactSupport: "HAUFFE kontaktieren",
    addDepartments: "Abteilungen erweitern",
    billingSelectionTitle: "Ihre Auswahl",
    billingAction: "Aktion",
    newPlan: "Neuer Plan",
    basePrice: "Grundpreis",
    addonPrice: "Zusatzabteilungen",
    discount15: "Rabatt 15%",
    totalPrice: "Gesamtpreis",
    vatNote: "zzgl. gesetzlicher Umsatzsteuer",
    stripeActivationNote: "Änderungen werden erst nach bestätigter Stripe-Zahlung aktiv.",
    bookSelection: "Auswahl aktualisieren",
    checkoutPrepared: "Checkout wird vorbereitet",
    dealWinSummaryPrepared: "Nach dem Kauf verwaltet der Account Owner die Zugänge und weist Lizenzen intern zu.",
    bundleCheckoutPrepared: "10 % Bundle-Vorteil auf Guided Digital Access vorgesehen. Der Bundle-Checkout wird vorbereitet.",
    guidedOnlyCheckoutNote: "Aktuell wird nur Guided Digital Access über diesen Checkout gebucht. Deal Win Engine Checkout wird separat gestartet.",
    separateCheckoutNote: "Guided Digital Access und Deal Win Engine werden aktuell über getrennte Checkouts gebucht.",
    dealWinBuyNow: "Deal Win Engine kaufen",
    extraDealWinUsers: "Zusätzliche Deal-Win-Zugänge",
    extraDealWinUsersProduct: "Zusätzliche Zugänge (Deal Win Engine)",
    extraDealWinUsersHelp: "Ergänzen Sie zusätzliche Zugänge zum ausgewählten Deal-Win-Engine-Team-Paket.",
    totalDealWinUsers: "Deal-Win-Zugänge gesamt",
    dealWinTotalPrice: "Deal Win Engine gesamt",
    checkoutStarting: "Checkout wird gestartet...",
    checkoutError: "Der Checkout konnte nicht gestartet werden.",
    executiveValueOpenTitle: "Geschäftsleitung: Beitrag zur Value-Runde offen",
    executiveValueOpenBody: "Die Geschäftsleitungsperspektive wurde eingereicht. Für die Kriterienbildung fehlt noch der Beitrag der Geschäftsleitung zur Value-Runde.",
    answerExecutiveValueRound: "Value-Runde als Geschäftsleitung beantworten",
    openExecutiveValueRound: "Geschäftsleitung zur Value-Runde öffnen",
    valueRoundContribution: "Beitrag zur Value-Runde",
    valueRoundContributionSubmitted: "Beitrag zur Value-Runde eingereicht",
    viewEditExecutivePerspective: "Geschäftsleitungsperspektive ansehen / bearbeiten",
    viewEditValueRoundContribution: "Beitrag zur Value-Runde ansehen / bearbeiten",
    executivePerspectiveOpenTitle: "Geschäftsleitungsperspektive noch offen",
    executivePerspectiveOpenBody: "Die Geschäftsleitungsperspektive existiert, wurde aber noch nicht eingereicht.",
    answerExecutivePerspective: "Geschäftsleitungsperspektive beantworten",
    ownerRoleTitle: "Eigene Rolle klären",
    ownerRoleBody: "Der Workspace Owner ist organisatorisch verantwortlich, aber nicht automatisch Geschäftsleitung. Wählen Sie, ob Sie selbst zusätzlich eine fachliche Perspektive einbringen.",
    ownerExecutive: "Geschäftsleitung auswählen",
    ownerExecutiveDescription: "Ich bringe die Perspektive der Geschäftsleitung ein.",
    ownerFunctional: "Fachlich stimmberechtigte Perspektive auswählen",
    ownerFunctionalDescription: "Ich bin nicht Geschäftsleitung, gebe aber eine fachlich relevante Perspektive ab.",
    ownerOrganizer: "Nur Workspace organisieren",
    ownerOrganizerDescription: "Ich verwalte den Workspace organisatorisch und gebe keine fachliche Perspektive ab.",
    selectedChoice: "Ausgewählt",
    ownerPerspectiveName: "Ihre Perspektive / Rolle",
    ownerPerspectiveType: "Perspektiventyp",
    saveOwnerRole: "Owner-Rolle speichern",
    ownerRoleSaved: "Owner-Rolle wurde gespeichert.",
    ownerRoleError: "Owner-Rolle konnte nicht gespeichert werden.",
    answerOwnPerspective: "Eigene Perspektive beantworten",
    ownerCurrentRole: "Aktuelle Owner-Perspektive",
    organizationalOnly: "Nur organisatorische Verantwortung",
    votingPerspective: "Stimmberechtigte Perspektive",
    executiveIncluded: "Geschäftsleitung ist inklusive und zählt nicht gegen das Abteilungslimit.",
    organizerOnlyNote: "Sie verwalten diesen Workspace organisatorisch und geben keine fachliche Perspektive ab.",
    perspectiveStatus: "Perspektivenstatus",
    draft: "Entwurf",
    submittedStatus: "Eingereicht",
    perspectiveLabels: {
      department: "Abteilung",
      executive: "Geschäftsleitung",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Weitere Perspektive"
    } satisfies Record<PerspectiveType, string>
  };
  const [overview, setOverview] = useState<RuntimeWorkspaceOverview>({ departments: [] });
  const [billingStatus, setBillingStatus] = useState<RuntimeBillingStatus | undefined>();
  const [billingPlans, setBillingPlans] = useState<RuntimeBillingPlan[]>([]);
  const [billingError, setBillingError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [ownerMode, setOwnerMode] = useState<"executive" | "functional" | "organizer_only">("organizer_only");
  const [ownerPerspectiveType, setOwnerPerspectiveType] = useState<PerspectiveType>("department");
  const [ownerPerspectiveName, setOwnerPerspectiveName] = useState("");
  const [isSavingOwnerRole, setIsSavingOwnerRole] = useState(false);
  const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | undefined>();
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedDealWinPlanId, setSelectedDealWinPlanId] = useState("");
  const [extraDepartmentCount, setExtraDepartmentCount] = useState(0);
  const [extraDealWinUserCount, setExtraDealWinUserCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function loadWorkspace() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextOverview = await fetchRuntimeWorkspace();
        const [nextBilling, nextPlans] = await Promise.all([
          fetchRuntimeBillingEntitlement().catch((caught: unknown) => {
            if (isMounted) setBillingError(caught instanceof Error ? caught.message : copy.billingNotAvailable);
            return undefined;
          }),
          fetchRuntimeBillingPlans().catch(() => undefined)
        ]);
        if (isMounted) {
          setOverview(nextOverview);
          setBillingStatus(nextBilling);
          setBillingPlans(nextPlans?.plans ?? []);
          if (!selectedPlanId && nextPlans?.plans.length) {
            setSelectedPlanId(nextBilling?.billing?.entitlement?.planId ?? nextPlans.recommendedPlanId);
          }
          const ownerPerspective = nextOverview.ownerPerspective;
          if (ownerPerspective) {
            if (ownerPerspective.isVotingPerspective === false) {
              setOwnerMode("organizer_only");
            } else if (ownerPerspective.perspectiveType === "executive") {
              setOwnerMode("executive");
            } else {
              setOwnerMode("functional");
            }
            setOwnerPerspectiveType(ownerPerspective.perspectiveType ?? "department");
            setOwnerPerspectiveName(readBilingualText(ownerPerspective.nameI18n ?? ownerPerspective.name, language));
          }
        }
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadWorkspace();

    return () => {
      isMounted = false;
    };
  }, [copy.billingNotAvailable, copy.loadError, language, selectedPlanId]);

  useEffect(() => {
    if (section !== "plans" || typeof window === "undefined") return;
    const moduleParam = new URLSearchParams(window.location.search).get("module");
    const targetId = moduleParam === "deal-win"
      ? "workspace-plans-deal-win"
      : moduleParam === "guided"
        ? "workspace-plans-guided"
        : undefined;
    if (!targetId) return;
    window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 80);
  }, [section]);

  const workspace = overview.workspace;
  const departments = overview.departments;
  const billableDepartments = departments.filter(isBillablePerspective);
  const submittedDepartments = departments.filter(
    (department) => department.status === "submitted" || department.status === "reviewed" || department.responseStatus === "submitted"
  ).length;
  const aiAvailable = Boolean(overview.latestSummary);
  const calculationAvailable = Boolean(overview.calculation);
  const accessMessage = workspace ? getWorkspaceAccessMessage(workspace.status, language) : undefined;
  const executivePerspective = getExecutivePerspective(departments);
  const ownerIsExecutive = Boolean(overview.ownerPerspective?.id && executivePerspective?.id === overview.ownerPerspective.id);
  const executiveInitialSubmitted = Boolean(
    executivePerspective &&
    (executivePerspective.responseStatus === "submitted" || executivePerspective.status === "submitted" || executivePerspective.status === "reviewed")
  );
  const executiveValueRoundOpen = Boolean(
    executivePerspective &&
    executiveInitialSubmitted &&
    executivePerspective.valueInputStatus !== "submitted" &&
    valueRoundCanAcceptInput(overview.process?.status)
  );
  const executiveInitialOpen = Boolean(executivePerspective && !executiveInitialSubmitted);
  const executiveTaskHref = executivePerspective
    ? ownerIsExecutive
      ? withLanguagePrefix(`/workspace/departments/${executivePerspective.id}/input${executiveValueRoundOpen ? "#value-round" : ""}`, language)
      : buildInviteLink(typeof window !== "undefined" ? window.location.origin : "", executivePerspective.inviteToken) + (executiveValueRoundOpen ? "#value-round" : "")
    : undefined;
  const ownerExecutiveInputHref = overview.ownerPerspective?.perspectiveType === "executive"
    ? withLanguagePrefix(`/workspace/departments/${overview.ownerPerspective.id}/input`, language)
    : undefined;
  const ownerExecutiveValueHref = overview.ownerPerspective?.perspectiveType === "executive"
    ? withLanguagePrefix(`/workspace/departments/${overview.ownerPerspective.id}/input#value-round`, language)
    : undefined;
  const ownerExecutiveSubmitted = overview.ownerPerspective?.perspectiveType === "executive" && (
    overview.ownerPerspective.responseStatus === "submitted" ||
    overview.ownerPerspective.status === "submitted" ||
    overview.ownerPerspective.status === "reviewed"
  );
  const ownerExecutiveValueSubmitted = overview.ownerPerspective?.perspectiveType === "executive" && overview.ownerPerspective.valueInputStatus === "submitted";
  const ownerExecutiveValueStatus = ownerExecutiveValueSubmitted
    ? copy.valueRoundContributionSubmitted
    : overview.ownerPerspective?.valueInputStatus === "draft"
      ? copy.draft
      : copy.open;
  const ownerPerspectiveStatus = overview.ownerPerspective?.responseStatus === "submitted"
    ? copy.submittedStatus
    : overview.ownerPerspective?.responseStatus === "draft" || overview.ownerPerspective?.valueInputStatus === "draft"
      ? copy.draft
      : copy.open;
  const remainingDays = workspace ? getRemainingDays(workspace.endDate) : 0;
  const setupComplete = workspace?.status === "active";
  const departmentsComplete = departments.length > 0 && submittedDepartments === departments.length;
  const invitedComplete = Boolean(workspace && billableDepartments.length >= workspace.maxDepartments);
  const nextStepTitle = calculationAvailable ? copy.resultAccess : copy.nextStep;
  const nextStepBody = departments.length === 0
    ? copy.noDepartments
    : submittedDepartments === 0
      ? copy.inviteFirst
      : !aiAvailable
        ? copy.requestEvaluation
        : !calculationAvailable
          ? copy.prepareCalculation
          : copy.useCalculation;

  async function saveOwnerRole() {
    setIsSavingOwnerRole(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const result = await configureRuntimeOwnerPerspective({
        mode: ownerMode,
        perspectiveType: ownerMode === "executive" ? "executive" : ownerPerspectiveType,
        name: ownerPerspectiveName,
        language
      });
      setOverview((current) => ({
        ...current,
        ownerPerspective: result.department,
        departments: current.departments.some((department) => department.id === result.department.id)
          ? current.departments.map((department) => department.id === result.department.id ? result.department : department)
          : [...current.departments, result.department]
      }));
      setNotice(copy.ownerRoleSaved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.ownerRoleError);
    } finally {
      setIsSavingOwnerRole(false);
    }
  }

  async function requestDeletion() {
    setIsRequestingDeletion(true);
    setError(undefined);
    try {
      await requestRuntimeWorkspaceContentDeletion();
      setNotice(copy.deletionRequested);
      const nextOverview = await fetchRuntimeWorkspace();
      setOverview(nextOverview);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.deletionError);
    } finally {
      setIsRequestingDeletion(false);
    }
  }

  async function openBilling(flow?: "invoices" | "payment-method" | "cancel-subscription" | "update-subscription") {
    setIsOpeningBillingPortal(true);
    setBillingError(undefined);
    try {
      const result = await openRuntimeBillingPortal(flow);
      window.location.href = result.portalUrl;
    } catch (caught) {
      setBillingError(caught instanceof Error ? caught.message : copy.billingPortalError);
    } finally {
      setIsOpeningBillingPortal(false);
    }
  }

  async function startCheckout(
    purpose: CheckoutPurpose,
    planId = selectedPlanId,
    discountAction?: "upgrade_15" | "renewal_15",
    extraUserCount = 0
  ) {
    if (!planId) return;
    setIsStartingCheckout(true);
    setCheckoutPlanId(planId);
    setBillingError(undefined);
    try {
      const result = await startRuntimeBillingCheckout({
        purpose,
        planId,
        extraDepartmentCount: Math.max(0, Math.trunc(extraDepartmentCount)),
        extraUserCount: Math.max(0, Math.trunc(extraUserCount)),
        discountAction
      });
      window.location.assign(result.checkoutUrl);
    } catch {
      setBillingError(copy.checkoutError);
    } finally {
      setIsStartingCheckout(false);
      setCheckoutPlanId(undefined);
    }
  }

  function formatEuro(value: number) {
    return new Intl.NumberFormat(language === "en" ? "en-US" : "de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0
    }).format(value);
  }

  function billingIntervalSuffix(plan: RuntimeBillingPlan): string {
    if (plan.billingInterval === "monthly") return copy.netPerMonth;
    if (plan.billingInterval === "every_3_months") return language === "en" ? "net / 3 months" : "netto / 3 Monate";
    if (plan.billingInterval === "every_6_months") return language === "en" ? "net / 6 months" : "netto / 6 Monate";
    return language === "en" ? "net / year" : "netto / Jahr";
  }

  function extraDepartmentIntervalLabel(plan: RuntimeBillingPlan): string {
    if (plan.billingInterval === "monthly") return copy.extraDepartmentMonthly;
    if (plan.billingInterval === "every_3_months") return language === "en" ? "net per additional department / 3 months" : "netto je zusätzliche Abteilung / 3 Monate";
    if (plan.billingInterval === "every_6_months") return language === "en" ? "net per additional department / 6 months" : "netto je zusätzliche Abteilung / 6 Monate";
    return language === "en" ? "net per additional department / year" : "netto je zusätzliche Abteilung / Jahr";
  }

  function dealWinMonthlyAmount(value: number | undefined): number {
    return Math.round((value ?? 0) / 12);
  }

  function dealWinMonthlySuffix(): string {
    return language === "en" ? "net / month" : "netto / Monat";
  }

  function dealWinAnnualEquivalent(value: number): string {
    return language === "en"
      ? `equals ${formatEuro(value)} net per year`
      : `entspricht ${formatEuro(value)} netto pro Jahr`;
  }

  const billing = billingStatus?.billing;
  const entitlement = billing?.entitlement;
  const productiveAccessAllowed = Boolean(
    billing?.accessAllowed ??
    billing?.productiveAccessAllowed ??
    entitlement?.accessAllowed ??
    entitlement?.productiveAccessAllowed
  );
  const moduleAccess = deriveWorkspaceModuleAccess({
    entitlement,
    entitlements: billing?.entitlements,
    packageType: workspace?.packageType,
    productiveAccessAllowed
  });
  const {
    hasGuidedAccess,
    hasDealWinAccess,
    canManageDepartments,
    canUseCustomerValueCalculation,
    canUseDealWinEngine
  } = moduleAccess;
  const canReactivateBilling = Boolean(billing?.canReactivate);
  const hasStripeCustomer = Boolean(entitlement?.providerCustomerId);
  const hasStripeSubscription = Boolean(entitlement?.providerSubscriptionId);
  const activeBillingPlan = billingPlans.find((plan) => plan.id === entitlement?.planId);
  const addonPriceText = activeBillingPlan
    ? `${formatEuro(activeBillingPlan.extraDepartmentPriceNetEur)} ${extraDepartmentIntervalLabel(activeBillingPlan)}`
    : undefined;
  const safeExtraDepartmentCount = Math.max(0, Math.trunc(Number.isFinite(extraDepartmentCount) ? extraDepartmentCount : 0));
  const addonTotalText = activeBillingPlan
    ? `${safeExtraDepartmentCount} × ${formatEuro(activeBillingPlan.extraDepartmentPriceNetEur)} = ${formatEuro(safeExtraDepartmentCount * activeBillingPlan.extraDepartmentPriceNetEur)}${
        ` ${billingIntervalSuffix(activeBillingPlan)}`
      }`
    : undefined;

  function billingPlanLabel(planId: string | undefined) {
    const labels: Record<string, string> = {
      guided_digital_access_3m: language === "en" ? "Guided Digital Access · 3 months" : "Guided Digital Access · 3 Monate",
      guided_digital_access_plus_6m: language === "en" ? "Guided Digital Access Plus · 6 months" : "Guided Digital Access Plus · 6 Monate",
      guided_digital_access_executive_12m: language === "en" ? "Guided Digital Access Executive · 12 months" : "Guided Digital Access Executive · 12 Monate",
      monthly_subscription: "Monthly Subscription"
    };
    return planId ? labels[planId] ?? planId : copy.notStored;
  }

  function billingSourceLabel(source: string | undefined) {
    const labels: Record<string, string> = {
      stripe_payment: language === "en" ? "Stripe payment" : "Stripe-Zahlung",
      admin_override: language === "en" ? "HAUFFE Admin grant" : "HAUFFE-Admin-Freigabe",
      manual_invoice: language === "en" ? "Manual invoice" : "Manuelle Rechnung",
      internal_demo: language === "en" ? "Internal demo access" : "Interner Demo-Zugang",
      migration: "Migration",
      support: language === "en" ? "Support grant" : "Support-Freigabe"
    };
    return source ? labels[source] ?? source : copy.notStored;
  }

  function billingStatusLabel(status: string | undefined) {
    const labels: Record<string, string> = {
      inactive: language === "en" ? "Inactive" : "Inaktiv",
      pending_checkout: language === "en" ? "Checkout pending" : "Checkout offen",
      payment_pending: language === "en" ? "Payment pending" : "Zahlung ausstehend",
      active: language === "en" ? "Active" : "Aktiv",
      expiring: language === "en" ? "Expiring" : "Läuft aus",
      cancelled_active_until_period_end: language === "en" ? "Active until period end" : "Aktiv bis Laufzeitende",
      expired: language === "en" ? "Expired" : "Abgelaufen",
      reactivatable: language === "en" ? "Reactivatable" : "Reaktivierbar",
      archived: language === "en" ? "Archived" : "Archiviert",
      deleted: language === "en" ? "Deleted" : "Gelöscht",
      payment_failed: language === "en" ? "Payment failed" : "Zahlung fehlgeschlagen",
      revoked: language === "en" ? "Revoked" : "Widerrufen"
    };
    return status ? labels[status] ?? status : copy.notStored;
  }

  function billingPlanAction(plan: RuntimeBillingPlan): {
    label: string;
    purpose?: CheckoutPurpose;
    disabled: boolean;
    kind: "current" | "positive" | "disabled" | "neutral";
    discountAction?: "upgrade_15" | "renewal_15";
    checkoutLabel?: string;
  } {
    const action = getBillingPlanAction({
      targetPlanId: plan.id,
      currentPlanId: entitlement?.planId,
      productiveAccessAllowed,
      canReactivate: canReactivateBilling,
      entitlementStatus: entitlement?.status,
      periodEnd: entitlement?.currentPeriodEnd ?? entitlement?.expiresAt
    });
    if (action === "current") return { label: copy.currentPlan, disabled: true, kind: "current" };
    if (action === "renew") return { label: copy.renewAccessSelect, purpose: "renewal", disabled: false, kind: "positive", discountAction: "renewal_15", checkoutLabel: copy.renewAccessCheckout };
    if (action === "upgrade") return { label: copy.upgradeAccessSelect, purpose: "upgrade", disabled: false, kind: "positive", discountAction: "upgrade_15", checkoutLabel: copy.upgradeAccessCheckout };
    if (action === "reactivate") return { label: copy.reactivateAccess, purpose: "reactivation", disabled: false, kind: "positive" };
    if (action === "buy") return { label: copy.buyAccess, purpose: "initial_purchase", disabled: false, kind: "neutral" };
    if (action === "downgrade_unavailable") {
      return { label: copy.downgradeUnavailable, disabled: true, kind: "disabled" };
    }
    if (action === "monthly_unavailable") return { label: copy.monthlyUnavailable, disabled: true, kind: "disabled" };
    return { label: copy.contactSupport, disabled: true, kind: "disabled" };
  }

  const selectedBillingPlan = billingPlans.find((plan) => plan.id === selectedPlanId);
  const dealWinPlans = getDealWinEnginePlans();
  const selectedDealWinPlan = dealWinPlans.find((plan) => plan.id === selectedDealWinPlanId);
  const bundleDiscountPreviewActive = Boolean(selectedDealWinPlan && selectedDealWinPlan.id !== "deal_win_engine_single_12m");
  const safeExtraDealWinUserCount = selectedDealWinPlan?.id === "deal_win_engine_single_12m" ? 0 : Math.max(0, Math.trunc(Number.isFinite(extraDealWinUserCount) ? extraDealWinUserCount : 0));
  const selectedDealWinTotalUsers = selectedDealWinPlan ? (selectedDealWinPlan.includedUsers ?? 1) + safeExtraDealWinUserCount : 0;
  const selectedDealWinMonthlyAddonUnitNetEur = selectedDealWinPlan ? dealWinMonthlyAmount(selectedDealWinPlan.extraUserPriceNetEur) : 0;
  const selectedDealWinAddonMonthlyNetEur = safeExtraDealWinUserCount * selectedDealWinMonthlyAddonUnitNetEur;
  const selectedDealWinAddonNetEur = selectedDealWinPlan ? safeExtraDealWinUserCount * (selectedDealWinPlan.extraUserPriceNetEur ?? 0) : 0;
  const selectedDealWinBaseMonthlyNetEur = selectedDealWinPlan ? dealWinMonthlyAmount(selectedDealWinPlan.priceNetEur) : 0;
  const selectedDealWinTotalMonthlyNetEur = selectedDealWinBaseMonthlyNetEur + selectedDealWinAddonMonthlyNetEur;
  const selectedDealWinTotalNetEur = selectedDealWinPlan ? selectedDealWinPlan.priceNetEur + selectedDealWinAddonNetEur : 0;
  const selectedBillingAction = selectedBillingPlan ? billingPlanAction(selectedBillingPlan) : undefined;
  const selectedBillingActionAvailable = Boolean(selectedBillingAction?.purpose && !selectedBillingAction.disabled && selectedBillingAction.kind !== "current");
  const selectedBillingAddonNetEur = selectedBillingPlan ? safeExtraDepartmentCount * selectedBillingPlan.extraDepartmentPriceNetEur : 0;
  const selectedBillingContractEnd = entitlement?.currentPeriodEnd ?? entitlement?.expiresAt ?? workspace?.endDate;
  const selectedBillingAmount = selectedBillingPlan && selectedBillingAction?.purpose && isBillingPlanId(selectedBillingPlan.id)
    ? calculateCheckoutAmount({
        planId: selectedBillingPlan.id,
        purpose: selectedBillingAction.purpose,
        extraDepartmentCount: safeExtraDepartmentCount,
        isBeforeExpiry: Boolean(selectedBillingContractEnd && new Date(selectedBillingContractEnd).getTime() > Date.now())
      })
    : undefined;
  const selectedBillingTotalNetEur = selectedBillingAmount?.totalNetEur ?? (selectedBillingPlan ? selectedBillingPlan.priceNetEur + selectedBillingAddonNetEur : 0);
  const selectedBillingButtonLabel = copy.bookSelection;
  const showBillingSelectionSummary = Boolean(selectedDealWinPlan || (selectedBillingPlan && selectedBillingActionAvailable && selectedBillingAction?.purpose));
  const guidedPlansHref = withLanguagePrefix("/workspace/plans?module=guided", language);
  const dealWinPlansHref = withLanguagePrefix("/workspace/plans?module=deal-win", language);

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={section === "plans" ? copy.myPlans : copy.title}
      description={section === "plans" ? copy.myPlansBody : copy.description}
    >
      {isLoading ? (
        <Panel title={copy.loadingTitle}>
          <HauffeLoader label={copy.loadingLabel} />
        </Panel>
      ) : null}

      {error ? (
        <Panel title={copy.loadErrorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}

      {!isLoading && !error && !workspace ? (
        <Panel title={copy.noWorkspaceTitle} variant="accent">
          <p>{copy.noWorkspaceBody}</p>
        </Panel>
      ) : null}

      {workspace ? (
        <>
          {section === "overview" ? (
          <>
          <div className="hero-actions">
            <a className="button button-primary" href={withLanguagePrefix("/workspace/setup", language)} aria-disabled={Boolean(accessMessage)}>
              {setupComplete ? copy.workspaceOverview : copy.setup}
            </a>
            {canManageDepartments ? <a className="button button-secondary" href={withLanguagePrefix("/workspace/departments", language)}>
              {copy.manageDepartments}
            </a> : null}
            {hasGuidedAccess ? <a className="button button-secondary" href={withLanguagePrefix("/workspace/hauffe-os-summary", language)}>
              {copy.evaluation}
            </a> : null}
            {canUseCustomerValueCalculation ? <a className="button button-secondary" href={withLanguagePrefix("/workspace/customer-value", language)}>
              {copy.calculation}
            </a> : null}
            {canUseDealWinEngine ? <a className="button button-secondary" href={withLanguagePrefix("/workspace/deal-win-engine", language)}>
              {copy.openDealWinEngine}
            </a> : null}
            <a className="button button-secondary" href={withLanguagePrefix("/workspace/plans", language)}>
              {copy.myPlans}
            </a>
          </div>

          {notice ? (
            <Panel title={copy.ownerRoleSaved} variant="soft">
              <p>{notice}</p>
            </Panel>
          ) : null}

          <div className="grid grid-4">
            <Stat label={copy.package} value={getPackageLabel(workspace.packageType, language)} />
            <Stat label={copy.status} value={workspaceStatusLabel(workspace.status, language)} tone={setupComplete ? "success" : workspace.status === "draft" ? "warning" : "danger"} />
            <Stat label={copy.remainingDays} value={String(remainingDays)} tone={remainingDays < 0 ? "danger" : remainingDays <= 7 ? "warning" : "success"} />
            {hasGuidedAccess ? (
              <>
                <Stat label={copy.invitedDepartments} value={`${billableDepartments.length} / ${workspace.maxDepartments}`} tone={invitedComplete ? "success" : "warning"} />
                <Stat label={copy.submitted} value={`${submittedDepartments} / ${departments.length}`} tone={departmentsComplete ? "success" : "warning"} />
                <Stat label={copy.evaluation} value={aiAvailable ? copy.created : copy.open} tone={aiAvailable ? "success" : "warning"} />
                <Stat label={copy.calculation} value={calculationAvailable ? copy.prepared : copy.open} tone={calculationAvailable ? "success" : "warning"} />
              </>
            ) : null}
            {hasDealWinAccess ? <Stat label={copy.dealWinSectionTitle} value={copy.moduleActive} tone="success" /> : null}
          </div>

          <Panel title={copy.currentGoal} variant="accent">
            <p>{readBilingualText(workspace.goalI18n ?? workspace.goal, language) || copy.noGoal}</p>
          </Panel>

          <Panel title={copy.moduleOverviewTitle} variant="accent">
            <p>{copy.moduleOverviewBody}</p>
            <div className="workspace-module-grid">
              <article className={`workspace-module-card ${hasGuidedAccess ? "workspace-module-card-active" : "workspace-module-card-upgrade"}`}>
                <div className="workspace-module-card__header">
                  <h3>{copy.guidedSectionTitle}</h3>
                  <StatusBadge tone={hasGuidedAccess ? "success" : "warning"} withCheck={hasGuidedAccess}>
                    {hasGuidedAccess ? copy.moduleActive : copy.moduleNotBooked}
                  </StatusBadge>
                </div>
                <p>{hasGuidedAccess ? copy.guidedModuleActiveText : copy.guidedModuleUpgradeText}</p>
                <div className="workspace-module-card__actions">
                  {hasGuidedAccess ? (
                    <>
                      <a className="button button-primary" href={withLanguagePrefix("/workspace/departments", language)}>{copy.manageDepartments}</a>
                      <a className="button button-secondary" href={withLanguagePrefix("/workspace/customer-value", language)}>{copy.moduleContinue}</a>
                    </>
                  ) : (
                    <a className="button button-gold" href={guidedPlansHref}>{copy.bookNow}</a>
                  )}
                </div>
              </article>
              <article className={`workspace-module-card ${hasDealWinAccess ? "workspace-module-card-active" : "workspace-module-card-upgrade"}`}>
                <div className="workspace-module-card__header">
                  <h3>{copy.dealWinSectionTitle}</h3>
                  <StatusBadge tone={hasDealWinAccess ? "success" : "warning"} withCheck={hasDealWinAccess}>
                    {hasDealWinAccess ? copy.moduleActive : copy.moduleNotBooked}
                  </StatusBadge>
                </div>
                <p>{hasDealWinAccess ? copy.dealWinModuleActiveText : copy.dealWinModuleUpgradeText}</p>
                <div className="workspace-module-card__actions">
                  {hasDealWinAccess ? (
                    <a className="button button-primary" href={withLanguagePrefix("/workspace/deal-win-engine", language)}>{copy.openDealWinEngine}</a>
                  ) : (
                    <a className="button button-gold" href={dealWinPlansHref}>{copy.bookNow}</a>
                  )}
                </div>
              </article>
            </div>
          </Panel>
          </>
          ) : null}

          {section === "plans" ? (
          <Panel title={copy.billingTitle} variant="accent">
            <p>{copy.billingBody}</p>
            {billingError ? <p className="form-error" role="alert">{billingError}</p> : null}
            <div className="data-list">
              <div>
                <span>{copy.billingAccess}</span>
                <strong>{productiveAccessAllowed ? copy.accessAllowed : copy.accessBlocked}</strong>
              </div>
              <div>
                <span>{copy.billingPlan}</span>
                <strong>{billingPlanLabel(entitlement?.planId)}</strong>
              </div>
              <div>
                <span>{copy.billingStatus}</span>
                <strong>{billingStatusLabel(entitlement?.status)}</strong>
              </div>
              <div>
                <span>{copy.billingSource}</span>
                <strong>{billingSourceLabel(entitlement?.accessSource)}</strong>
              </div>
              <div>
                <span>{copy.billingPeriod}</span>
                <strong>
                  {entitlement?.currentPeriodStart && entitlement.currentPeriodEnd
                    ? `${entitlement.currentPeriodStart.slice(0, 10)} – ${entitlement.currentPeriodEnd.slice(0, 10)}`
                    : copy.notStored}
                </strong>
              </div>
              <div>
                <span>{copy.billingDepartments}</span>
                <strong>{entitlement?.maxDepartmentCount ?? workspace.maxDepartments}</strong>
              </div>
            </div>
            <div className="billing-portal-actions">
              <button className="button button-gold" type="button" disabled={isOpeningBillingPortal || !hasStripeCustomer} onClick={() => void openBilling("invoices")}>
                {copy.openInvoices}
              </button>
              {hasStripeCustomer ? (
                <button className="button button-secondary" type="button" disabled={isOpeningBillingPortal} onClick={() => void openBilling("payment-method")}>
                  {copy.changePaymentMethod}
                </button>
              ) : null}
              {hasStripeSubscription ? (
                <>
                  <button className="button button-secondary" type="button" disabled={isOpeningBillingPortal} onClick={() => void openBilling("update-subscription")}>
                    {copy.manageSubscription}
                  </button>
                  <button className="button button-danger" type="button" disabled={isOpeningBillingPortal} onClick={() => void openBilling("cancel-subscription")}>
                    {copy.cancelSubscription}
                  </button>
                </>
              ) : null}
            </div>
            {!hasStripeCustomer && entitlement ? <p className="form-note">{copy.billingPortalUnavailable}</p> : null}
            {hasStripeCustomer && !hasStripeSubscription ? <p className="form-note">{copy.billingSubscriptionUnavailable}</p> : null}
            {billingPlans.length > 0 ? (
              <>
                <h3 id="workspace-plans-guided">{copy.guidedSectionTitle}</h3>
                <p className="form-note">{copy.billingCheckoutNote}</p>
                <div className="grid grid-4 billing-plan-grid">
                  {billingPlans.map((plan) => {
                    const action = billingPlanAction(plan);
                    const cardStateClass = action.kind === "current"
                      ? "billing-plan-card-current"
                      : action.kind === "disabled"
                        ? "billing-plan-card-disabled"
                        : selectedPlanId === plan.id
                          ? "billing-plan-card-selected"
                          : "";
                    return (
                      <GuidedPlanCard
                        key={plan.id}
                        className={cardStateClass}
                        badge={plan.isBestValue ? <StatusBadge tone="success">{copy.bestValue}</StatusBadge> : null}
                        title={billingPlanLabel(plan.id)}
                        description={plan.description}
                        price={formatEuro(plan.priceNetEur)}
                        priceSuffix={billingIntervalSuffix(plan)}
                        features={`${plan.includedDepartments} ${copy.includedDepartments} · ${formatEuro(plan.extraDepartmentPriceNetEur)} ${extraDepartmentIntervalLabel(plan)}`}
                        note={copy.executiveIncludedShort}
                        addonConfig={selectedPlanId === plan.id && action.purpose && !action.disabled ? (
                          <div className="pricing-card-addon-control">
                            <label>
                              {copy.extraDepartmentsProduct}
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={extraDepartmentCount}
                                onChange={(event) => setExtraDepartmentCount(Math.max(0, Math.trunc(Number(event.target.value) || 0)))}
                              />
                            </label>
                            {safeExtraDepartmentCount > 0 ? (
                              <p>{safeExtraDepartmentCount} × {formatEuro(plan.extraDepartmentPriceNetEur)} = {formatEuro(selectedBillingAddonNetEur)} {billingIntervalSuffix(plan)}</p>
                            ) : null}
                          </div>
                        ) : null}
                        action={action.kind === "current" || action.kind === "disabled" ? (
                            <div className={`billing-plan-status ${action.kind === "current" ? "billing-plan-status-current" : "billing-plan-status-disabled"}`}>
                              <span>{action.label}</span>
                            </div>
                        ) : (
                            <button
                              className={`button billing-plan-action-button ${selectedPlanId === plan.id ? "button-primary" : action.kind === "positive" ? "button-gold" : "button-secondary"}`}
                              type="button"
                              disabled={isStartingCheckout || action.disabled}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setSelectedPlanId(plan.id);
                              }}
                            >
                              {isStartingCheckout && checkoutPlanId === plan.id
                                ? copy.checkoutStarting
                                : action.kind === "positive"
                                  ? action.label
                                  : selectedPlanId === plan.id
                                    ? copy.selectedChoice
                                    : action.label}
                            </button>
                        )}
                      />
                    );
                  })}
                </div>
                <div className="billing-module-section">
                  <h3 id="workspace-plans-deal-win">{copy.dealWinSectionTitle}</h3>
                  <p className="form-note">{bundleDiscountPreviewActive ? copy.bundleDiscountActive : copy.bundleDiscountHint}</p>
                  <div className="deal-win-pricing-grid">
                    {dealWinPlans.map((plan) => (
                      <DealWinPlanCard
                        key={plan.id}
                        plan={plan}
                        language={language}
                        selected={selectedDealWinPlanId === plan.id}
                        singleBadge={<span className="pricing-card__badge">{copy.dealWinSingleBadge}</span>}
                        bundleBadge={<StatusBadge tone="warning">{copy.dealWinBundleBadge}</StatusBadge>}
                        includedUsersLabel={copy.includedUsers}
                        durationLabel={language === "en" ? "months" : "Monate"}
                        intervalLabel={billingIntervalSuffix(plan)}
                        note={copy.dealWinCheckoutPrepared}
                        addonConfig={selectedDealWinPlanId === plan.id && plan.id !== "deal_win_engine_single_12m" ? (
                          <div className="pricing-card-addon-control">
                            <label>
                              {copy.extraDealWinUsersProduct}
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={extraDealWinUserCount}
                                onChange={(event) => setExtraDealWinUserCount(Math.max(0, Math.trunc(Number(event.target.value) || 0)))}
                              />
                            </label>
                            {safeExtraDealWinUserCount > 0 ? (
                              <p>{safeExtraDealWinUserCount} × {formatEuro(dealWinMonthlyAmount(plan.extraUserPriceNetEur))} = {formatEuro(safeExtraDealWinUserCount * dealWinMonthlyAmount(plan.extraUserPriceNetEur))} {dealWinMonthlySuffix()}</p>
                            ) : null}
                          </div>
                        ) : null}
                        action={(
                          <button
                            className={`button billing-plan-action-button ${selectedDealWinPlanId === plan.id ? "button-primary" : "button-secondary"}`}
                            type="button"
                            onClick={() => {
                              if (selectedDealWinPlanId !== plan.id) {
                                setSelectedDealWinPlanId(plan.id);
                                setExtraDealWinUserCount(0);
                              }
                            }}
                          >
                            {selectedDealWinPlanId === plan.id ? copy.selectedChoice : copy.dealWinSelect}
                          </button>
                        )}
                      />
                    ))}
                  </div>
                </div>
                {showBillingSelectionSummary ? (
                  <div className="billing-selection-summary">
                    <h3>{copy.billingSelectionTitle}</h3>
                    <div className="purchase-summary">
                      <dl>
                        {selectedBillingPlan && selectedBillingActionAvailable && selectedBillingAction?.purpose ? (
                          <>
                            <div>
                              <dt>{copy.billingAction}</dt>
                              <dd>{selectedBillingAction.label}</dd>
                            </div>
                            <div>
                              <dt>{copy.newPlan}</dt>
                              <dd>{billingPlanLabel(selectedBillingPlan.id)}</dd>
                            </div>
                            <div>
                              <dt>{copy.includedDepartments}</dt>
                              <dd>{selectedBillingPlan.includedDepartments}</dd>
                            </div>
                            <div>
                              <dt>{copy.extraDepartments}</dt>
                              <dd>{safeExtraDepartmentCount}</dd>
                            </div>
                            <div>
                              <dt>{copy.basePrice}</dt>
                              <dd>{formatEuro(selectedBillingPlan.priceNetEur)}</dd>
                            </div>
                            <div>
                              <dt>{copy.extraDepartmentPrice}</dt>
                              <dd>{formatEuro(selectedBillingPlan.extraDepartmentPriceNetEur)} {extraDepartmentIntervalLabel(selectedBillingPlan)}</dd>
                            </div>
                            <div>
                              <dt>{copy.addonPrice}</dt>
                              <dd>{safeExtraDepartmentCount > 0 ? `${safeExtraDepartmentCount} × ${formatEuro(selectedBillingPlan.extraDepartmentPriceNetEur)} = ${formatEuro(selectedBillingAddonNetEur)}` : "—"}</dd>
                            </div>
                            {selectedBillingAmount && selectedBillingAmount.discountNetEur > 0 ? (
                              <div>
                                <dt>{copy.discount15}</dt>
                                <dd>-{formatEuro(selectedBillingAmount.discountNetEur)}</dd>
                              </div>
                            ) : null}
                            <div className="purchase-summary__total">
                              <dt>{copy.totalPrice}</dt>
                              <dd>{formatEuro(selectedBillingTotalNetEur)} {billingIntervalSuffix(selectedBillingPlan)}</dd>
                            </div>
                          </>
                        ) : null}
                        {selectedDealWinPlan ? (
                          <>
                            <div>
                              <dt>{copy.dealWinSectionTitle}</dt>
                              <dd>{selectedDealWinPlan.displayName}</dd>
                            </div>
                            <div>
                              <dt>{copy.includedUsers}</dt>
                              <dd>{selectedDealWinPlan.includedUsers ?? 1}</dd>
                            </div>
                            <div>
                              <dt>{copy.extraDealWinUsers}</dt>
                              <dd>{safeExtraDealWinUserCount}</dd>
                            </div>
                            <div>
                              <dt>{copy.totalDealWinUsers}</dt>
                              <dd>{selectedDealWinTotalUsers}</dd>
                            </div>
                            <div className="purchase-summary__total">
                              <dt>{copy.dealWinTotalPrice}</dt>
                              <dd>
                                <span>{formatEuro(selectedDealWinTotalMonthlyNetEur)} {dealWinMonthlySuffix()}</span>
                                <small>{dealWinAnnualEquivalent(selectedDealWinTotalNetEur)}</small>
                              </dd>
                            </div>
                          </>
                        ) : null}
                      </dl>
                      <p>{copy.vatNote}</p>
                      {selectedDealWinPlan ? <p className="form-note">{bundleDiscountPreviewActive ? copy.bundleCheckoutPrepared : copy.dealWinSummaryPrepared}</p> : null}
                      {selectedDealWinPlan && selectedBillingPlan && selectedBillingActionAvailable ? <p className="form-note">{copy.separateCheckoutNote}</p> : null}
                      <p>{copy.stripeActivationNote}</p>
                      <div className="form-actions">
                        {selectedBillingPlan && selectedBillingActionAvailable && selectedBillingAction?.purpose ? (
                          <button className="button button-primary" type="button" disabled={isStartingCheckout} onClick={() => void startCheckout(selectedBillingAction.purpose!, selectedBillingPlan.id, selectedBillingAction.discountAction)}>
                            {isStartingCheckout && checkoutPlanId === selectedBillingPlan.id ? copy.checkoutStarting : selectedBillingButtonLabel}
                          </button>
                        ) : null}
                        {selectedDealWinPlan ? (
                          <button className="button button-gold" type="button" disabled={isStartingCheckout} onClick={() => void startCheckout("initial_purchase", selectedDealWinPlan.id, undefined, safeExtraDealWinUserCount)}>
                            {isStartingCheckout && checkoutPlanId === selectedDealWinPlan.id ? copy.checkoutStarting : copy.dealWinBuyNow}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
                {productiveAccessAllowed && !selectedBillingActionAvailable ? (
                  <div className="billing-addon-card">
                    <div>
                      <h3>{copy.extraDepartments}</h3>
                      <p>{copy.extraDepartmentsHelp}</p>
                      {addonPriceText ? <p className="billing-addon-price">{addonPriceText}</p> : null}
                    </div>
                    <div className="form form-grid">
                    <label>
                      {copy.extraDepartmentsLabel}
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={extraDepartmentCount}
                        onChange={(event) => setExtraDepartmentCount(Math.max(0, Math.trunc(Number(event.target.value) || 0)))}
                      />
                    </label>
                    {addonTotalText ? (
                      <div className="billing-addon-summary">
                        <span>{copy.extraDepartments}</span>
                        <strong>{addonTotalText}</strong>
                      </div>
                    ) : null}
                    <div className="form-actions">
                      <button className="button button-secondary" type="button" disabled={isStartingCheckout || safeExtraDepartmentCount <= 0 || !activeBillingPlan} onClick={() => activeBillingPlan ? void startCheckout("add_department", activeBillingPlan.id) : undefined}>
                        {copy.addDepartments}
                      </button>
                    </div>
                  </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </Panel>
          ) : (
          <Panel title={copy.billingOverviewTitle} variant="accent">
            <p>{copy.billingOverviewBody}</p>
            {billingError ? <p className="form-error" role="alert">{billingError}</p> : null}
            <div className="data-list">
              <div>
                <span>{copy.billingAccess}</span>
                <strong>{productiveAccessAllowed ? copy.accessAllowed : copy.accessBlocked}</strong>
              </div>
              <div>
                <span>{copy.billingPlan}</span>
                <strong>{billingPlanLabel(entitlement?.planId)}</strong>
              </div>
              <div>
                <span>{copy.billingPeriod}</span>
                <strong>
                  {entitlement?.currentPeriodStart && entitlement.currentPeriodEnd
                    ? `${entitlement.currentPeriodStart.slice(0, 10)} – ${entitlement.currentPeriodEnd.slice(0, 10)}`
                    : copy.notStored}
                </strong>
              </div>
            </div>
            <div className="form-actions">
              <a className="button button-primary" href={withLanguagePrefix("/workspace/plans", language)}>
                {copy.openMyPlans}
              </a>
            </div>
          </Panel>
          )}

          {section === "overview" ? (
          <>
          <Panel title={copy.retentionTitle} variant="accent">
            {(() => {
              const noticeText = retentionNotice(language, workspace.retention);
              const retentionExpired = Boolean(workspace.retention?.retentionUntil && workspace.retention.retentionUntil < new Date().toISOString().slice(0, 10));
              const status = workspace.retention?.deletionStatus === "deleted"
                ? copy.deleted
                : workspace.retention?.deletionStatus === "requested"
                  ? copy.deletionRequestedStatus
                  : retentionExpired
                    ? copy.retentionExpired
                    : copy.active;
              return (
                <>
                  <p>{noticeText.body}</p>
                  <div className="data-list">
                    <div>
                      <span>{copy.status}</span>
                      <strong>{status}</strong>
                    </div>
                    <div>
                      <span>{language === "en" ? "Retention until" : "Aufbewahrung bis"}</span>
                      <strong>{workspace.retention?.retentionUntil ?? copy.notStored}</strong>
                    </div>
                    <div>
                      <span>{copy.active}</span>
                      <strong>{overview.contentLibrary?.activeItems ?? 0}</strong>
                    </div>
                    <div>
                      <span>{copy.deletionRequestedStatus}</span>
                      <strong>{overview.contentLibrary?.deletionRequestedItems ?? 0}</strong>
                    </div>
                    <div>
                      <span>{copy.deleted}</span>
                      <strong>{overview.contentLibrary?.deletedItems ?? 0}</strong>
                    </div>
                    <div>
                      <span>{copy.repairNeeded}</span>
                      <strong>{overview.contentLibrary?.repairNeeded ?? 0}</strong>
                    </div>
                  </div>
                  {workspace.retention?.deletionStatus !== "requested" && workspace.retention?.deletionStatus !== "deleted" ? (
                    <button className="button button-secondary" type="button" disabled={isRequestingDeletion} onClick={() => void requestDeletion()}>
                      {copy.deletionRequest}
                    </button>
                  ) : null}
                </>
              );
            })()}
          </Panel>

          {accessMessage ? (
            <Panel title={copy.workspaceNotice} variant="accent">
              <p>{accessMessage}</p>
            </Panel>
          ) : null}

          {hasGuidedAccess ? (
          <>
          <Panel title={copy.ownerRoleTitle} variant="accent">
            <p>{copy.ownerRoleBody}</p>
            <div className="owner-choice-group" role="radiogroup" aria-label={copy.ownerRoleTitle}>
              <label className={`owner-choice-card ${ownerMode === "executive" ? "owner-choice-card-selected" : ""}`}>
                <input
                  className="owner-choice-input"
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === "executive"}
                  onChange={() => {
                    setOwnerMode("executive");
                    setOwnerPerspectiveType("executive");
                    setOwnerPerspectiveName(language === "en" ? "Executive Management" : "Geschäftsleitung");
                  }}
                />
                <span className="owner-choice-check" aria-hidden="true">{ownerMode === "executive" ? "✓" : ""}</span>
                <span className="owner-choice-copy">
                  <strong>{copy.ownerExecutive}</strong>
                  <small>{copy.ownerExecutiveDescription}</small>
                </span>
                {ownerMode === "executive" ? <span className="owner-choice-chip">{copy.selectedChoice}</span> : null}
              </label>
              <label className={`owner-choice-card ${ownerMode === "functional" ? "owner-choice-card-selected" : ""}`}>
                <input
                  className="owner-choice-input"
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === "functional"}
                  onChange={() => setOwnerMode("functional")}
                />
                <span className="owner-choice-check" aria-hidden="true">{ownerMode === "functional" ? "✓" : ""}</span>
                <span className="owner-choice-copy">
                  <strong>{copy.ownerFunctional}</strong>
                  <small>{copy.ownerFunctionalDescription}</small>
                </span>
                {ownerMode === "functional" ? <span className="owner-choice-chip">{copy.selectedChoice}</span> : null}
              </label>
              <label className={`owner-choice-card ${ownerMode === "organizer_only" ? "owner-choice-card-selected" : ""}`}>
                <input
                  className="owner-choice-input"
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === "organizer_only"}
                  onChange={() => {
                    setOwnerMode("organizer_only");
                    setOwnerPerspectiveType("other");
                    setOwnerPerspectiveName(language === "en" ? "Project Coordination" : "Projektleitung");
                  }}
                />
                <span className="owner-choice-check" aria-hidden="true">{ownerMode === "organizer_only" ? "✓" : ""}</span>
                <span className="owner-choice-copy">
                  <strong>{copy.ownerOrganizer}</strong>
                  <small>{copy.ownerOrganizerDescription}</small>
                </span>
                {ownerMode === "organizer_only" ? <span className="owner-choice-chip">{copy.selectedChoice}</span> : null}
              </label>
            </div>
            <div className="form form-grid owner-role-form">
              {ownerMode === "functional" ? (
                <>
                  <label>
                    {copy.ownerPerspectiveType}
                    <select
                      value={ownerPerspectiveType}
                      onChange={(event) => setOwnerPerspectiveType(event.target.value as PerspectiveType)}
                    >
                      {(["department", "finance", "commercial", "operations", "other"] as PerspectiveType[]).map((type) => (
                        <option key={type} value={type}>{copy.perspectiveLabels[type]}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {copy.ownerPerspectiveName}
                    <input
                      value={ownerPerspectiveName}
                      onChange={(event) => setOwnerPerspectiveName(event.target.value)}
                      placeholder={language === "en" ? "e.g. Marketing, Finance, Sales" : "z. B. Marketing, Finance, Sales"}
                    />
                  </label>
                </>
              ) : null}
              <div className="owner-role-status form-wide">
                <div>
                  <span>{copy.ownerCurrentRole}</span>
                  <strong>
                    {overview.ownerPerspective
                      ? `${copy.perspectiveLabels[overview.ownerPerspective.perspectiveType ?? "department"]} · ${overview.ownerPerspective.isVotingPerspective === false ? copy.organizationalOnly : copy.votingPerspective}`
                      : copy.notStored}
                  </strong>
                </div>
                <div>
                  <span>{copy.perspectiveStatus}</span>
                  <strong>{overview.ownerPerspective ? ownerPerspectiveStatus : copy.open}</strong>
                </div>
                <div>
                  <span>{language === "en" ? "Executive / department limit" : "Geschäftsleitung / Abteilungslimit"}</span>
                  <strong>{ownerMode === "executive" ? copy.executiveIncluded : ownerMode === "organizer_only" ? copy.organizerOnlyNote : copy.votingPerspective}</strong>
                </div>
              </div>
              <div className="form-actions form-wide">
                <button className="button button-primary" type="button" onClick={() => void saveOwnerRole()} disabled={isSavingOwnerRole}>
                  {copy.saveOwnerRole}
                </button>
                {ownerExecutiveInputHref ? (
                  <a className="button button-secondary" href={ownerExecutiveInputHref}>
                    {ownerExecutiveSubmitted ? copy.viewEditExecutivePerspective : copy.answerExecutivePerspective}
                  </a>
                ) : overview.ownerPerspective && overview.ownerPerspective.isVotingPerspective !== false ? (
                  <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${overview.ownerPerspective.id}/input`, language)}>
                    {copy.answerOwnPerspective}
                  </a>
                ) : null}
              </div>
              {ownerExecutiveValueHref ? (
                <div className="form-wide owner-value-task">
                  <div className="owner-value-task__header">
                    <h3>{copy.valueRoundContribution}</h3>
                    <StatusBadge tone={ownerExecutiveValueSubmitted ? "success" : "warning"} withCheck={ownerExecutiveValueSubmitted}>
                      {ownerExecutiveValueStatus}
                    </StatusBadge>
                  </div>
                  <p>
                    {ownerExecutiveValueSubmitted
                      ? copy.valueRoundContributionSubmitted
                      : copy.executiveValueOpenBody}
                  </p>
                  <a className="button button-primary owner-value-task__button" href={ownerExecutiveValueHref}>
                    {ownerExecutiveValueSubmitted ? copy.viewEditValueRoundContribution : copy.answerExecutiveValueRound}
                  </a>
                </div>
              ) : null}
            </div>
          </Panel>

          {(executiveValueRoundOpen || executiveInitialOpen) && executiveTaskHref ? (
            <Panel title={copy.openTasks} variant="accent">
              <h2>{executiveValueRoundOpen ? copy.executiveValueOpenTitle : copy.executivePerspectiveOpenTitle}</h2>
              <p>{executiveValueRoundOpen ? copy.executiveValueOpenBody : copy.executivePerspectiveOpenBody}</p>
              <div className="hero-actions">
                <a className="button button-primary" href={executiveTaskHref}>
                  {executiveValueRoundOpen
                    ? ownerIsExecutive ? copy.answerExecutiveValueRound : copy.openExecutiveValueRound
                    : copy.answerExecutivePerspective}
                </a>
              </div>
            </Panel>
          ) : null}

          <Panel title={nextStepTitle}>
            <StatusBadge tone={calculationAvailable ? "success" : departments.length === 0 || submittedDepartments === 0 ? "danger" : "warning"} withCheck={calculationAvailable}>
              {calculationAvailable ? copy.viewCalculation : !aiAvailable ? copy.requestEvaluation : copy.createCalculation}
            </StatusBadge>
            <p>{nextStepBody}</p>
            <div className="hero-actions">
              <a className="button button-primary" href={withLanguagePrefix(calculationAvailable ? "/workspace/customer-value" : !aiAvailable ? "/workspace/hauffe-os-summary" : "/workspace/customer-value", language)}>
                {calculationAvailable ? copy.viewCalculation : !aiAvailable ? copy.requestEvaluation : copy.createCalculation}
              </a>
            </div>
          </Panel>

          <div className="workspace-process-grid">
            {[
              {
                numeral: "I",
                title: copy.stepSetup,
                badge: setupComplete ? copy.complete : workspaceStatusLabel(workspace.status, language),
                tone: setupComplete ? "success" : workspace.status === "draft" ? "warning" : "danger",
                complete: setupComplete,
                body: undefined
              },
              {
                numeral: "II",
                title: copy.stepDepartments,
                badge: departmentsComplete ? copy.complete : copy.incomplete,
                tone: departmentsComplete ? "success" : "warning",
                complete: departmentsComplete,
                body: departments.length === 0 ? copy.noDepartments : copy.perspectivesSubmitted(submittedDepartments, departments.length)
              },
              {
                numeral: "III",
                title: copy.stepEvaluation,
                badge: aiAvailable ? copy.evaluationAvailable : copy.open,
                tone: aiAvailable ? "success" : "warning",
                complete: aiAvailable,
                body: aiAvailable ? undefined : copy.noEvaluation
              },
              {
                numeral: "IV",
                title: copy.stepCalculation,
                badge: calculationAvailable ? copy.calculationPrepared : copy.open,
                tone: calculationAvailable ? "success" : "warning",
                complete: calculationAvailable,
                body: calculationAvailable ? undefined : copy.noCalculation
              }
            ].map((step) => (
              <section className="workspace-process-card" key={step.numeral}>
                <div className="workspace-process-card__numeral" aria-hidden="true">{step.numeral}</div>
                <div className="workspace-process-card__content">
                  <h3>{step.title}</h3>
                  <StatusBadge tone={step.tone as "success" | "warning" | "danger"} withCheck={step.complete}>
                    {step.badge}
                  </StatusBadge>
                  {step.body ? <p>{step.body}</p> : null}
                </div>
              </section>
            ))}
          </div>
          </>
          ) : null}

          {hasDealWinAccess ? (
            <Panel title={copy.dealWinSectionTitle} variant="accent">
              <p>{copy.dealWinModuleActiveText}</p>
              <div className="hero-actions">
                <a className="button button-primary" href={withLanguagePrefix("/workspace/deal-win-engine", language)}>
                  {copy.openDealWinEngine}
                </a>
              </div>
            </Panel>
          ) : null}
          </>
          ) : null}
        </>
      ) : null}
    </Layout>
  );
}
